import api from './api';

// ---------------------------------------------------------------------------
// 🔓 آزادسازی وجه فروشنده (اسکرو) — سیاست هر فروشگاه + دخالت دستی ادمین
// ---------------------------------------------------------------------------
export interface EscrowPolicy {
  digitalDays: number;
  postalDays: number;
  physicalDays: number;
  global: { digitalDays: number; postalDays: number; physicalDays: number };
  source: { digital: 'store' | 'global'; postal: 'store' | 'global'; physical: 'store' | 'global' };
  custom: boolean;
  note?: string;
  updatedAt?: string;
  updatedByName?: string;
}

export type EscrowBucket = 'escrow' | 'awaiting_delivery' | 'released' | 'blocked';

export interface EscrowItem {
  orderItemId: string;
  orderId: string;
  orderNumber?: string;
  productTitle: string;
  quantity: number;
  amount: number;
  fulfillmentType?: string;
  inspected: boolean;
  paidAt?: string | null;
  deliveredAt?: string | null;
  releaseDate?: string | null;
  daysLeft: number | null;
  warrantyDays: number;
  bucket: EscrowBucket;
  overridden: boolean;
  override?: { kind: string; releaseAt: string; reason?: string; byName?: string; at: string } | null;
  blockedReason?: string;
  /** دستی آزاد شده ولی مانعِ اصلی هنوز سر جایش است */
  releasedEarly?: boolean;
  riskNote?: string;
}

export interface EscrowOverview {
  policy: EscrowPolicy;
  totals: { escrow: number; awaitingDelivery: number; released: number; blocked: number; overridden: number; releasedEarly: number };
  items: EscrowItem[];
  balance: {
    availableBalance: number;
    pendingBalance: number;
    blockedAmount: number;
    awaitingDeliveryAmount: number;
    debt: number;
    refundObligationsTotal: number;
    totalWithdrawn: number;
  } | null;
}

export type ReleaseMode = 'items' | 'amount' | 'all';
export interface ReleaseInput {
  mode: ReleaseMode;
  itemIds?: string[];
  amount?: number;
  includeAwaitingDelivery?: boolean;
  includeBlocked?: boolean;
  reason?: string;
}

export interface ReleasePlan {
  rows: EscrowItem[];
  totalAmount: number;
  count: number;
  requestedAmount?: number;
  shortfall: number;
  warnings: string[];
  policy: EscrowPolicy;
}

export const BUCKET_META: Record<EscrowBucket, { label: string; color: 'green' | 'orange' | 'red' | 'blue' | 'gray'; hint: string }> = {
  escrow: { label: 'در دورهٔ گارانتی', color: 'orange', hint: 'تحویل شده ولی هنوز دورهٔ گارانتی تمام نشده' },
  awaiting_delivery: { label: 'در انتظار تحویل', color: 'blue', hint: 'پول گرفته شده ولی کالا هنوز به خریدار نرسیده' },
  released: { label: 'آزاد شده', color: 'green', hint: 'قابل برداشت است' },
  blocked: { label: 'مسدود', color: 'red', hint: 'مانعی دارد (اختلاف باز، پرداخت ناتمام، تحویل ناقص، بازرسی ناتمام) و تا رفع مانع خودبه‌خود آزاد نمی‌شود' },
};

const unwrap = (r: any) => (r && typeof r === 'object' && 'data' in r && 'status' in r ? r.data : r);
const fail = (r: any, fallback: string) => {
  if (r && r.status === 'error') throw new Error(r.message || fallback);
  return r;
};

const storeEscrowService = {
  /** وضعیت کامل: سیاست، موجودی لحظه‌ای، اقلام */
  overview: (storeId: string, includeReleased = false) =>
    api.get<EscrowOverview>(`/admin/stores/${storeId}/escrow${includeReleased ? '?includeReleased=1' : ''}`),

  /** تغییر مدت گارانتی اختصاصی (null در هر فیلد = پیروی از سراسری) */
  savePolicy: async (storeId: string, body: { digitalDays: number | null; postalDays: number | null; physicalDays: number | null; note?: string }) => {
    const r: any = await api.patch(`/admin/stores/${storeId}/escrow/policy`, body);
    return unwrap(fail(r, 'خطا در ذخیرهٔ مدت گارانتی')) as EscrowPolicy;
  },

  /** پیش‌نمایش آزادسازی — هیچ تغییری اعمال نمی‌شود */
  preview: async (storeId: string, body: ReleaseInput) => {
    const r: any = await api.post(`/admin/stores/${storeId}/escrow/preview`, body);
    return unwrap(fail(r, 'خطا در پیش‌نمایش')) as ReleasePlan;
  },

  /** اعمال آزادسازی */
  release: async (storeId: string, body: ReleaseInput) => {
    const r: any = await api.post(`/admin/stores/${storeId}/escrow/release`, body);
    fail(r, 'خطا در آزادسازی');
    return { message: r?.message as string, data: unwrap(r) as { applied: number; amount: number; plan: ReleasePlan } };
  },

  /** تعویق آزادسازی */
  hold: async (storeId: string, body: { itemIds: string[]; days?: number; until?: string; reason?: string }) => {
    const r: any = await api.post(`/admin/stores/${storeId}/escrow/hold`, body);
    fail(r, 'خطا در تعویق');
    return { message: r?.message as string, data: unwrap(r) as { applied: number; amount: number; until: string } };
  },

  /** برگرداندن اقلام به قاعدهٔ عادی */
  clear: async (storeId: string, itemIds: string[]) => {
    const r: any = await api.post(`/admin/stores/${storeId}/escrow/clear`, { itemIds });
    fail(r, 'خطا در برگرداندن');
    return { message: r?.message as string, data: unwrap(r) as { applied: number } };
  },
};

export default storeEscrowService;
